import * as i2 from '@angular/common';
import { CommonModule, NgClass } from '@angular/common';
import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Injector, Renderer2, ElementRef, ViewChild, Output, Input, Inject, Component, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import * as i3 from '@angular/forms';
import { NG_VALUE_ACCESSOR, DefaultValueAccessor, NgControl, Validators, NgModel, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CentricIcon2Component, CentricIcon2Module } from '@windmill/ng-windmill/icon';
import * as i1 from '@angular/cdk/a11y';

const CENTRIC_TEXTAREA_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => Textarea2Component),
    multi: true
};
const NUMBER_OF_ROWS = 4;
const SCROLL_CLASS_NAME = 'has-scroll';
/**
 * To use this component in a module, you can import the `CentricTextarea2Module`:
 * @example
 * ```typescript
 * import { CentricTextarea2Module } from '@windmill/ng-windmill/textarea';
 * ```
 *
 * To use the standalone component directly, you can import `Textarea2Component`:
 * @example
 * ```typescript
 * import { Textarea2Component } from '@windmill/ng-windmill/textarea';
 * ```
 */
class Textarea2Component extends DefaultValueAccessor {
    injector;
    focusMonitor;
    /**
     * The id for textarea
     */
    id;
    /**
     *  Data-testid attribute for component
     */
    testTextareaId;
    /**
     * Placeholder text for textarea component
     */
    placeholder;
    /**
     *  Title text of the component
     */
    title;
    /**
     * Size of the component
     */
    size;
    /**
     * The rows number of the textarea
     */
    rows = NUMBER_OF_ROWS;
    /**
     * Whether the component is readonly
     */
    readonly = false;
    /**
     * Whether the component has resize
     */
    hasResize = false;
    /**
     * Could be used for adding a custom function for validation for errors of component
     */
    validationFunction;
    /**
     * The aria-label value for input
     */
    ariaLabel;
    /**
     * The aria-labelledby value for input
     */
    ariaLabelledby;
    /**
     * The aria-description value for input
     */
    ariaDescription;
    /**
     * The aria-describedby value for input
     */
    ariaDescribedby;
    /**
     * The aria-label value for clear icon
     */
    ariaLabelClearIcon;
    /**
     * Custom CSS background color (e.g. hex, rgba, CSS variable etc.) for the textarea in an editable state
     */
    backgroundColor;
    /**
     * Emits when the typed value of the textarea will change
     */
    textareaValueChanged = new EventEmitter();
    /**
     * Emits when the value of the textarea will be deleted
     */
    clearValue = new EventEmitter();
    textarea;
    value;
    elementRef;
    disabled = false;
    resizeObserver;
    centricTextareaElement;
    textareaElement;
    focusMonitorSubscription;
    get isInvalidTextarea() {
        if (typeof this.validationFunction !== 'undefined') {
            return this.validationFunction();
        }
        return false;
    }
    get backgroundColorValue() {
        return this.readonly || this.disabled ? null : this.backgroundColor;
    }
    get showClearButton() {
        return Boolean(this.value && !this.disabled && !this.readonly);
    }
    get showValidationMessages() {
        return this.isInvalidTextarea && !this.disabled && !this.readonly;
    }
    constructor(injector, render2, elementRef, focusMonitor) {
        super(render2, elementRef, false);
        this.injector = injector;
        this.focusMonitor = focusMonitor;
        this.elementRef = elementRef;
    }
    ngOnInit() {
        this.initializeTextareaElement();
    }
    ngAfterViewInit() {
        this.observeResize();
        this.focusMonitorSubscription = this.focusMonitor.monitor(this.textareaElement, true).subscribe();
    }
    ngOnDestroy() {
        this.focusMonitor.stopMonitoring(this.textareaElement);
        this.focusMonitorSubscription?.unsubscribe();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
    getFormControl() {
        return this.injector.get(NgControl).control;
    }
    isRequired() {
        return this.getFormControl().hasValidator(Validators.required) && !this.disabled && !this.readonly;
    }
    writeValue(newValue) {
        this.value = newValue;
        this.updateScrollBehavior();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    onValueChange(event) {
        const newValue = event.target.value;
        this.writeValue(newValue);
        if (this.injector.get(NgControl) instanceof NgModel) {
            this.onChange(newValue);
        }
        this.textareaValueChanged.emit(newValue);
        event.stopPropagation();
    }
    onClearValue() {
        this.getFormControl().markAsDirty();
        this.getFormControl().patchValue(null);
        this.textarea.nativeElement.focus();
        this.clearValue.emit(false);
    }
    getPlaceholder() {
        if (this.readonly && !this.value) {
            return '-';
        }
        if (!this.placeholder) {
            return '';
        }
        return this.placeholder;
    }
    initializeTextareaElement() {
        this.centricTextareaElement = this.elementRef.nativeElement;
        this.textareaElement = this.centricTextareaElement?.querySelector('textarea');
    }
    observeResize() {
        this.resizeObserver = new ResizeObserver(() => {
            this.updateScrollBehavior();
        });
        this.resizeObserver.observe(this.textareaElement);
    }
    updateScrollBehavior() {
        setTimeout(() => {
            const hasScroll = this.textareaElement?.scrollHeight > this.textareaElement?.clientHeight;
            if (hasScroll) {
                this.textareaElement?.classList.add(SCROLL_CLASS_NAME);
            }
            else if (this.textareaElement?.classList.contains(SCROLL_CLASS_NAME)) {
                this.textareaElement?.classList.remove(SCROLL_CLASS_NAME);
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Textarea2Component, deps: [{ token: forwardRef(() => Injector) }, { token: forwardRef(() => Renderer2) }, { token: forwardRef(() => ElementRef) }, { token: i1.FocusMonitor }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "19.2.10", type: Textarea2Component, isStandalone: true, selector: "centric-textarea2", inputs: { id: "id", testTextareaId: "testTextareaId", placeholder: "placeholder", title: "title", size: "size", rows: "rows", readonly: "readonly", hasResize: "hasResize", validationFunction: "validationFunction", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", ariaDescription: "ariaDescription", ariaDescribedby: "ariaDescribedby", ariaLabelClearIcon: "ariaLabelClearIcon", backgroundColor: "backgroundColor" }, outputs: { textareaValueChanged: "textareaValueChanged", clearValue: "clearValue" }, providers: [CENTRIC_TEXTAREA_VALUE_ACCESSOR], viewQueries: [{ propertyName: "textarea", first: true, predicate: ["textareaElement"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<div\r\n  class=\"centric-textarea\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.no-value]=\"!value\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [class.is-invalid]=\"isInvalidTextarea\"\r\n  [ngClass]=\"size\"\r\n  [style.backgroundColor]=\"backgroundColorValue\">\r\n  <textarea\r\n    #textareaElement\r\n    [formControl]=\"getFormControl()\"\r\n    [rows]=\"rows\"\r\n    [class.has-resize]=\"hasResize\"\r\n    [attr.id]=\"id\"\r\n    [attr.data-testid]=\"testTextareaId\"\r\n    [attr.placeholder]=\"getPlaceholder()\"\r\n    [attr.title]=\"title\"\r\n    [attr.aria-required]=\"isRequired()\"\r\n    [attr.aria-invalid]=\"isInvalidTextarea\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [readonly]=\"readonly\"\r\n    (input)=\"onValueChange($event)\"\r\n    (blur)=\"onTouched()\">\r\n  </textarea>\r\n  @if (showClearButton) {\r\n    <button\r\n      class=\"clear-icon-button\"\r\n      type=\"button\"\r\n      [attr.aria-label]=\"ariaLabelClearIcon\"\r\n      (click)=\"onClearValue()\">\r\n      <centric-icon2\r\n        class=\"clear-icon\"\r\n        [svgIcon]=\"'cancel_r'\"\r\n        [size]=\"'16'\">\r\n      </centric-icon2>\r\n    </button>\r\n  }\r\n</div>\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (showValidationMessages) {\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  }\r\n</div>\r\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i3.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: CentricIcon2Component, selector: "centric-icon2", inputs: ["size", "inline", "svgIcon"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: Textarea2Component, decorators: [{
            type: Component,
            args: [{ selector: 'centric-textarea2', imports: [CommonModule, FormsModule, NgClass, ReactiveFormsModule, CentricIcon2Component], providers: [CENTRIC_TEXTAREA_VALUE_ACCESSOR], standalone: true, template: "<div\r\n  class=\"centric-textarea\"\r\n  [class.is-disabled]=\"disabled\"\r\n  [class.no-value]=\"!value\"\r\n  [class.is-readonly]=\"readonly\"\r\n  [class.is-invalid]=\"isInvalidTextarea\"\r\n  [ngClass]=\"size\"\r\n  [style.backgroundColor]=\"backgroundColorValue\">\r\n  <textarea\r\n    #textareaElement\r\n    [formControl]=\"getFormControl()\"\r\n    [rows]=\"rows\"\r\n    [class.has-resize]=\"hasResize\"\r\n    [attr.id]=\"id\"\r\n    [attr.data-testid]=\"testTextareaId\"\r\n    [attr.placeholder]=\"getPlaceholder()\"\r\n    [attr.title]=\"title\"\r\n    [attr.aria-required]=\"isRequired()\"\r\n    [attr.aria-invalid]=\"isInvalidTextarea\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    [attr.aria-labelledby]=\"ariaLabelledby\"\r\n    [attr.aria-description]=\"ariaDescription\"\r\n    [attr.aria-describedby]=\"ariaDescribedby\"\r\n    [readonly]=\"readonly\"\r\n    (input)=\"onValueChange($event)\"\r\n    (blur)=\"onTouched()\">\r\n  </textarea>\r\n  @if (showClearButton) {\r\n    <button\r\n      class=\"clear-icon-button\"\r\n      type=\"button\"\r\n      [attr.aria-label]=\"ariaLabelClearIcon\"\r\n      (click)=\"onClearValue()\">\r\n      <centric-icon2\r\n        class=\"clear-icon\"\r\n        [svgIcon]=\"'cancel_r'\"\r\n        [size]=\"'16'\">\r\n      </centric-icon2>\r\n    </button>\r\n  }\r\n</div>\r\n<div class=\"states-messages\">\r\n  <ng-content select=\"[hints]\"></ng-content>\r\n  @if (showValidationMessages) {\r\n    <ng-content select=\"[errors]\"></ng-content>\r\n  }\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.Injector, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Injector)]
                }] }, { type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Renderer2)]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }, { type: i1.FocusMonitor }], propDecorators: { id: [{
                type: Input
            }], testTextareaId: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], title: [{
                type: Input
            }], size: [{
                type: Input
            }], rows: [{
                type: Input
            }], readonly: [{
                type: Input
            }], hasResize: [{
                type: Input
            }], validationFunction: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelClearIcon: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], textareaValueChanged: [{
                type: Output
            }], clearValue: [{
                type: Output
            }], textarea: [{
                type: ViewChild,
                args: ['textareaElement']
            }] } });

const moduleComponents = [Textarea2Component];
class CentricTextarea2Module {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricTextarea2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricTextarea2Module, imports: [CommonModule, FormsModule, ReactiveFormsModule, CentricIcon2Module, Textarea2Component], exports: [Textarea2Component] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricTextarea2Module, imports: [CommonModule, FormsModule, ReactiveFormsModule, CentricIcon2Module, moduleComponents] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricTextarea2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule, ReactiveFormsModule, CentricIcon2Module, ...moduleComponents],
                    exports: [...moduleComponents],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CentricTextarea2Module, Textarea2Component };
